import type { FlowDefinition, FlowNode, FlowTriggerKind, FlowTriggerNode } from '@reflex/flows';

/** All trigger nodes in a flow's graph, in node-list order. */
export function getTriggerNodes(flow: Pick<FlowDefinition, 'nodes'>): FlowTriggerNode[] {
  return flow.nodes.filter((n): n is FlowTriggerNode => n.type === 'trigger');
}

/** Trigger-kind facet value: a concrete kind or `none` for trigger-less flows. */
export type FlowTriggerFilterKind = FlowTriggerKind | 'none';

const TRIGGER_FILTER_KINDS: readonly FlowTriggerFilterKind[] = [
  'manual',
  'cron',
  'event',
  'service',
  'none',
];

/**
 * The set of trigger kinds a flow carries, for facet filtering. Trigger-less
 * flows yield `{'none'}`. Legacy configs with unrecognized kinds contribute
 * nothing — they match no kind facet but are not `none` either (mirrors
 * `summarizeTriggerNode`'s defensive `default:` branch).
 */
export function getFlowTriggerKinds(
  flow: Pick<FlowDefinition, 'nodes'>,
): Set<FlowTriggerFilterKind> {
  const triggers = getTriggerNodes(flow);
  if (triggers.length === 0) return new Set(['none']);
  const kinds = new Set<FlowTriggerFilterKind>();
  for (const trigger of triggers) {
    const kind = trigger.config.kind as string;
    if (TRIGGER_FILTER_KINDS.includes(kind as FlowTriggerFilterKind) && kind !== 'none') {
      kinds.add(kind as FlowTriggerFilterKind);
    }
  }
  return kinds;
}

/**
 * Parse a comma-separated URL param into trigger-kind facet values. Unknown
 * tokens are dropped silently; an empty result means "All".
 */
export function parseTriggerKindTokens(raw: string | null | undefined): FlowTriggerFilterKind[] {
  if (!raw) return [];
  const seen = new Set<FlowTriggerFilterKind>();
  for (const token of raw.split(',')) {
    const trimmed = token.trim() as FlowTriggerFilterKind;
    if (TRIGGER_FILTER_KINDS.includes(trimmed)) seen.add(trimmed);
  }
  return [...seen];
}

/** Short human label for one trigger node: `Manual`, `Cron · 0 * * * *`, `slack · slack_message`. */
export function summarizeTriggerNode(node: Pick<FlowTriggerNode, 'config'>): string {
  const config = node.config;
  switch (config.kind) {
    case 'manual':
      return 'Manual';
    case 'cron':
      return `Cron · ${config.schedule}`;
    case 'event':
      return config.action
        ? `${config.source} · ${config.event}.${config.action}`
        : `${config.source} · ${config.event}`;
    case 'service':
      return config.label ?? 'Service';
    default:
      // Legacy or unrecognized trigger configs fall back to a generic label
      // rather than returning `undefined` and rendering a blank trigger.
      return 'Trigger';
  }
}

/**
 * Label for a whole flow's trigger story: the single trigger's summary, a
 * count for multi-trigger flows, or `No trigger` for legacy/in-progress
 * graphs.
 */
export function getFlowTriggerLabel(flow: Pick<FlowDefinition, 'nodes'>): string {
  const triggers = getTriggerNodes(flow);
  if (triggers.length === 0) return 'No trigger';
  if (triggers.length === 1) return summarizeTriggerNode(triggers[0]);
  return `${triggers.length} triggers`;
}

/** True when the flow has at least one trigger node a Run button can fire. */
export function hasTriggerNode(flow: Pick<FlowDefinition, 'nodes'>): boolean {
  return flow.nodes.some((n: FlowNode) => n.type === 'trigger');
}
